/* After Five - product card media (v15). Works on every .af-card, on any page.

   1. Card video. Never autoplays a 1080p MP4. A card shows its poster image; the video is
      fetched only when it is about to play, from the HLS stream where the browser can play
      it natively and otherwise from an MP4 rendition; either way the smallest rendition
      that fills the card (480p or 720p), never 1080p.
        - touch / no hover: the one video most in view plays, all others are paused. On a
          card whose video is a later photo slide that means: it starts, muted and inline,
          when the shopper swipes to it, and stops when she swipes on or scrolls it away.
          A pause / play button sits on the slide. Pausing holds that one video until she
          plays it or swipes away from it; it is not remembered for other cards or pages
          (v14 kept it for the whole tab, so one tap stopped every card video from starting).
        - the button shows what was asked for, not what the network has delivered: from
          the moment a video is asked to play it is the pause button, also while the
          video's files are still on their way (on a slow connection that is seconds; v14
          showed "play" during that time, and a tap on it cancelled the start).
        - nothing here waits for the page's load event: the script runs when the page is
          parsed, and the playlist is asked for when the swipe begins, so play() is called
          the moment the slide is in view.
        - mouse: plays while the card is hovered.
        - Save-Data: nothing plays by itself; the button starts it (with a mouse the button is
          always on the card, top-right, clear of "Quick add").
        - prefers-reduced-motion no longer holds a video back (owner, 5 Oct 2026: her phone has
          Reduce Motion on, the live theme plays there and this one did not; the shoppable-video
          previews made the same change). The pause button on the card is that shopper's
          control (WCAG 2.2.2).
        - where the browser refuses a start that has no tap behind it (iOS Low Power Mode),
          the button shows "play" and one tap starts the video.
   2. Photo swipe (cards with .af-card--swipe; below 750px, and on touch screens of any width:
      a tablet has no hover to bring up the second photo or the video with). The extra photos
      are listed in data-af-extra. Nothing is fetched for a photo the shopper has not asked
      for: a card holds its first two photos; the other slides are added when she first
      touches, scrolls or focuses that card's photos, and each of them gets its file only when
      it is the next one along. Slides are real links: a tap opens the product, a drag swipes,
      vertical scrolling is untouched.
   3. Card rating (Klaviyo Reviews star widget). Cards that arrive after Klaviyo has started
      (sort, filter, "Load more", recommendations) are handed to it, and what it renders is
      made one named image instead of an unnamed group or a second link. Klaviyo also writes
      the same <style> into every widget; one copy is kept for the page (see "rating"). */
(function () {
  if (window.afCardMedia) return;

  var mqHover = window.matchMedia('(hover: hover) and (pointer: fine)');
  /* The opposite of "(min-width: 750px) and (hover: hover)" in assets/component-card.css */
  var mqSwipe = window.matchMedia('(max-width: 749px), (hover: none)');
  var saveData = !!(navigator.connection && navigator.connection.saveData);
  var IN_VIEW = 0.6;
  var ratios = new WeakMap();
  var visible = new Set();
  var current = null;
  var productCache = {};

  /* v14 remembered a pause for the whole tab under this key; a tab that still holds it is freed */
  try { sessionStorage.removeItem('afCardVideoPaused'); } catch (e) {}

  function autoplayAllowed() {
    return !saveData;
  }
  function cardOf(el) { return el.closest('.af-card'); }
  function toggleOf(video) {
    var card = cardOf(video);
    return card ? card.querySelector('[data-af-video-toggle]') : null;
  }
  function ratioOf(video) { return ratios.get(video) || 0; }

  /* ---- sources: the smallest rendition that still fills the card, never 1080p ---- */
  function pick(video, list) {
    /* list: [{ w, h, url }] */
    if (!list.length) return null;
    list.sort(function (a, b) { return a.w - b.w; });
    var allowed = list.filter(function (v) { return v.h <= 720; });
    if (!allowed.length) allowed = [list[0]];
    var box = video.closest('.af-card__media');
    /* 0.6: a 175px phone card at 2x asks for 210px and gets the 270px-wide 480p stream; at 0.8 it
       asked for 280px and fell through to 720p (4.5 Mbps) for a 10px difference */
    var target = (box ? box.clientWidth : 320) * Math.min(window.devicePixelRatio || 1, 2) * 0.6;
    for (var i = 0; i < allowed.length; i++) if (allowed[i].w >= target) return allowed[i].url;
    return allowed[allowed.length - 1].url;
  }

  function hlsVariant(video, master) {
    var base = new URL(master, window.location.href);
    return fetch(base.toString())
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (text) {
        var lines = text.split(/\r?\n/), list = [];
        for (var i = 0; i < lines.length - 1; i++) {
          var m = /^#EXT-X-STREAM-INF:.*RESOLUTION=(\d+)x(\d+)/.exec(lines[i]);
          var next = lines[i + 1].trim();
          if (m && next && next.charAt(0) !== '#') list.push({ w: +m[1], h: +m[2], url: new URL(next, base).toString() });
        }
        return pick(video, list) || base.toString();
      })
      .catch(function () { return base.toString(); });
  }

  function mp4Rendition(video) {
    var link = video.closest('a[href*="/products/"]');
    var fallback = video.getAttribute('data-mp4');
    if (!link || !window.fetch) return Promise.resolve(fallback);
    var handle = link.getAttribute('href').split('/products/')[1].split(/[?#]/)[0];
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    if (!productCache[handle]) {
      productCache[handle] = fetch(root + 'products/' + handle + '.js')
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    }
    var id = video.getAttribute('data-media-id');
    return productCache[handle].then(function (product) {
      var list = [];
      ((product && product.media) || []).forEach(function (m) {
        if (String(m.id) !== String(id) || !m.sources) return;
        m.sources.forEach(function (s) {
          if (s.format === 'mp4' || s.mime_type === 'video/mp4') list.push({ w: s.width, h: s.height, url: s.url });
        });
      });
      return pick(video, list) || fallback;
    });
  }

  function ensureSource(video) {
    if (video._afSource) return video._afSource;
    var hls = video.getAttribute('data-hls');
    var native = hls && window.fetch && (video.canPlayType('application/vnd.apple.mpegurl') || video.canPlayType('application/x-mpegURL'));
    video._afSource = (native ? hlsVariant(video, hls) : mp4Rendition(video)).then(function (url) {
      if (!url) throw new Error('no video source');
      /* preload="none" stays on the element: the address alone fetches nothing until play() */
      video.src = url;
      video._afReady = true;
      return url;
    });
    /* A failed look-up is not kept: the next request tries again */
    video._afSource.catch(function () { video._afSource = null; });
    return video._afSource;
  }

  /* ---- play / pause ---- */
  /* The button shows what was asked for: "pause" from the moment the video is asked to play,
     also while its files are still arriving (data-state "loading": the icon pulses, see
     assets/component-card.css). */
  function reflect(video) {
    var btn = toggleOf(video);
    if (!btn) return;
    var playing = !!video._afWant;
    btn.setAttribute('data-state', playing ? (video._afLive ? 'playing' : 'loading') : 'paused');
    /* The two labels are printed by snippets/card-product.liquid from the locale file */
    var label = btn.getAttribute(playing ? 'data-af-label-pause' : 'data-af-label-play');
    if (label) btn.setAttribute('aria-label', label);
  }

  function pause(video, reset) {
    if (!video) return;
    video._afWant = false;
    video._afLive = false;
    if (!video.paused) video.pause();
    if (reset) {
      try { video.currentTime = 0; } catch (e) {}
      video.classList.remove('is-playing');
    }
    if (current === video) current = null;
    reflect(video);
  }

  /* The browser's answer to play(). A refusal (no tap behind the request: iOS Low Power Mode)
     leaves the poster and the "play" button; a tap on it starts the video. */
  function start(video) {
    var p;
    try { p = video.play(); } catch (e) { p = null; }
    if (p && p.catch) {
      p.catch(function (error) {
        if (!video._afWant || !video.paused) return;
        /* AbortError: this request was replaced by a newer one (a pause, a new source) */
        if (error && error.name === 'AbortError') return;
        video._afWant = false;
        reflect(video);
      });
    }
  }

  function play(video) {
    if (document.hidden) return;
    if (current && current !== video) pause(current);
    current = video;
    video._afWant = true;
    video.muted = true;
    reflect(video);
    /* With the source in place play() is called here and now: inside the tap when there was one,
       and the instant the slide is in view when there was not. */
    if (video._afReady) { start(video); return; }
    ensureSource(video).then(function () {
      if (video._afWant) start(video);
    }).catch(function () {
      if (!video._afWant) return;
      video._afWant = false;
      if (current === video) current = null;
      reflect(video);
    });
  }

  /* Is the card showing this video's slide? (A swiped-away slide is not; a card scrolled off
     the screen still is.) */
  function onSlide(video) {
    var slide = video.parentNode;
    var track = slide && slide.parentNode;
    if (!track || !track.clientWidth) return true;
    return Math.abs(slide.offsetLeft - track.scrollLeft) < track.clientWidth / 2;
  }

  function bindVideo(video) {
    if (video._afBound) return;
    video._afBound = true;
    video.addEventListener('playing', function () {
      video._afLive = true;
      video.classList.add('is-playing');
      reflect(video);
    });
    video.addEventListener('pause', function () {
      /* Paused by the browser, not by this script (an interruption): the button offers "play" */
      video._afLive = false;
      if (video._afWant) {
        video._afWant = false;
        if (current === video) current = null;
      }
      reflect(video);
    });
    if (io) io.observe(video);
  }

  /* ---- touch: play the one video most in view ---- */
  function choose() {
    if (mqHover.matches) return;
    var best = null, bestRatio = IN_VIEW;
    visible.forEach(function (video) {
      var r = ratioOf(video);
      /* A video the shopper paused stays paused while its slide is in view */
      if (r >= bestRatio && !video._afHeld) { best = video; bestRatio = r; }
      var btn = toggleOf(video);
      if (btn) {
        var show = r >= IN_VIEW;
        if (btn.hidden === show) { btn.hidden = !show; reflect(video); }
      }
    });
    if (current && ratioOf(current) < IN_VIEW) pause(current);
    if (!autoplayAllowed()) return;
    if (best && best !== current) play(best);
  }

  var io = 'IntersectionObserver' in window
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          var video = e.target;
          /* The hidden second slide of a card in a carousel still intersects: skip it */
          var shown = e.isIntersecting && getComputedStyle(video.parentNode).opacity !== '0';
          var ratio = shown ? e.intersectionRatio : 0;
          ratios.set(video, ratio);
          /* Swiped away from: her pause ends with the visit to the slide. (A slide just out of
             view still counts as intersecting, with a ratio of 0: its edge touches the card's.) */
          if (video._afHeld && ratio < IN_VIEW && !onSlide(video)) video._afHeld = false;
          if (shown) {
            visible.add(video);
          } else {
            visible.delete(video);
            var btn = toggleOf(video);
            if (btn && !mqHover.matches) btn.hidden = true;
            /* A video the shopper started with the button stops when it is scrolled away */
            if (mqHover.matches && current === video && !e.isIntersecting) pause(video, true);
          }
        });
        choose();
      }, { threshold: [0, 0.3, 0.6, 0.9] })
    : null;

  /* ---- mouse: play while hovered ---- */
  function bindHover(card) {
    if (card._afHover) return;
    card._afHover = true;
    var video = card.querySelector('[data-af-video]');
    if (!video) return;
    var btn = card.querySelector('[data-af-video-toggle]');
    /* Nothing plays by itself for these shoppers, so the play button is on the card from the
       start: the keyboard reaches it too (it used to appear only while the mouse was over). */
    function manual() { return saveData; }
    function showManual() {
      if (btn && mqHover.matches && manual()) { btn.hidden = false; reflect(video); }
    }
    showManual();
    card.addEventListener('mouseenter', function () {
      if (!mqHover.matches) return;
      if (manual()) { showManual(); return; }
      play(video);
    });
    card.addEventListener('mouseleave', function () {
      if (!mqHover.matches) return;
      if (manual()) {
        /* Started by the shopper: it keeps playing until she pauses it */
        return;
      }
      if (btn) btn.hidden = true;
      pause(video, true);
    });
  }

  document.addEventListener('click', function (event) {
    var btn = event.target.closest && event.target.closest('[data-af-video-toggle]');
    if (!btn) return;
    event.preventDefault();
    var card = cardOf(btn);
    var video = card && (card.querySelector('[data-af-video].is-playing') || card.querySelector('[data-af-video]'));
    if (!video) return;
    /* What was asked for decides, not what has arrived: a tap while the video is still loading
       pauses it, as the button says */
    if (video._afWant || !video.paused) {
      video._afHeld = true;
      pause(video);
    } else {
      video._afHeld = false;
      play(video);
    }
  });

  document.addEventListener('visibilitychange', function () {
    if (document.hidden) { if (current) pause(current); } else choose();
  });

  /* ---- photo swipe (phones, touch tablets) ---- */
  function makeFocusable(track) {
    /* The swipe area is a scrolling region: reachable and scrollable from the keyboard too */
    track.tabIndex = 0;
    track.setAttribute('role', 'group');
    /* "<title> photos", printed by snippets/card-product.liquid from the locale file */
    var label = track.getAttribute('data-af-label');
    if (label) track.setAttribute('aria-label', label);
  }

  /* One more slide for the track. With "deferred" the photo's address waits in data-af-srcset
     until the slide is the next one along (showNext); an image inside a sideways scroller counts
     as near the screen for loading="lazy", so the attribute alone fetched every photo of every
     card that came near the screen. */
  function addSlide(track, url, deferred) {
    var first = track.querySelector('a.af-card__slide');
    var img0 = first.querySelector('img');
    var a = document.createElement('a');
    a.className = 'af-card__slide af-card__slide--extra';
    a.href = first.getAttribute('href');
    a.tabIndex = -1;
    a.setAttribute('aria-hidden', 'true');
    var img = document.createElement('img');
    img.className = 'af-card__image';
    img.alt = '';
    img.decoding = 'async';
    img.sizes = (img0 && img0.getAttribute('sizes')) || '50vw';
    var srcset = [360, 533, 720].map(function (w) {
      return url.replace(/([?&])width=\d+/, '$1width=' + w) + ' ' + w + 'w';
    }).join(', ');
    if (deferred) {
      img.setAttribute('data-af-srcset', srcset);
      img.setAttribute('data-af-src', url);
    } else {
      img.loading = 'lazy';
      img.srcset = srcset;
      img.src = url;
    }
    a.appendChild(img);
    track.appendChild(a);
  }

  /* The photo in view and the one after it have their files; the ones further along wait */
  function showNext(track) {
    var slides = track.querySelectorAll('.af-card__slide');
    var index = Math.round(track.scrollLeft / (track.clientWidth || 1));
    for (var i = 0; i < slides.length && i <= index + 1; i++) {
      var img = slides[i].querySelector('img[data-af-srcset]');
      if (!img) continue;
      img.srcset = img.getAttribute('data-af-srcset');
      img.src = img.getAttribute('data-af-src');
      img.removeAttribute('data-af-srcset');
      img.removeAttribute('data-af-src');
    }
  }

  var SWIPE_STARTS = ['touchstart', 'pointerdown', 'wheel', 'scroll', 'focus'];

  /* The card is near the screen. It needs one photo after the first, which the card's own markup
     usually holds already (its second photo); the rest wait for the shopper (addRest). */
  function addSlides(track) {
    if (track._afExtra) return;
    track._afExtra = true;
    var urls = (track.getAttribute('data-af-extra') || '').split('|').filter(Boolean);
    if (!urls.length || !track.querySelector('a.af-card__slide')) return;
    if (track.querySelectorAll('.af-card__slide').length < 2) addSlide(track, urls.shift(), false);
    if (!urls.length) return;
    function addRest() {
      SWIPE_STARTS.forEach(function (type) { track.removeEventListener(type, addRest); });
      urls.forEach(function (url) { addSlide(track, url, true); });
      track.addEventListener('scroll', function () { showNext(track); }, { passive: true });
      showNext(track);
    }
    SWIPE_STARTS.forEach(function (type) { track.addEventListener(type, addRest, { passive: true }); });
  }

  var nearIO = 'IntersectionObserver' in window
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          nearIO.unobserve(e.target);
          addSlides(e.target);
        });
      }, { rootMargin: '400px 0px' })
    : null;

  /* ---- rating ----
     Klaviyo fills the rating elements it finds when it starts. When that start came from its
     5-second timer it does not look at the page again, so cards added later kept an empty
     rating. renderWidgets() is its own entry point; it fills only what is still empty. */
  var ratingTimer = 0;
  function askForRatings() {
    clearTimeout(ratingTimer);
    ratingTimer = setTimeout(function () {
      var kl = window.klReviewsObject;
      if (kl && typeof kl.renderWidgets === 'function') kl.renderWidgets();
    }, 50);
  }

  /* What Klaviyo renders is stars and a count inside an element with an aria-label and no role,
     so the label was not announced; on product pages that element is also a link to the reviews
     of the product being viewed (#fulfilled-reviews-all): a second tab stop on every card, named
     after one product and leading to another's reviews. On a card, and in the quick-add panel,
     it is an image with a name; the card's link is the way to the product. */
  var RATING = '.af-card__rating, quick-add-modal .klaviyo-star-rating-widget';

  /* Klaviyo writes the same <style>@layer base{:root{--kl-reviews-stars-...}}</style> into every
     star widget it renders. With two identical copies in the page, every stylesheet or <link>
     added afterwards (Shopify's checkout preloader adds about 160) made the browser restyle and
     lay out the whole document: about 3 seconds of main-thread work on a phone. The rules only
     set custom properties on :root, so one copy styles every widget: the first copy of each text
     moves to the <head>, where no re-render of a grid or a product section can take it away,
     and the copies inside the widgets go. */
  var layerStyles = window.afRatingStyles || (window.afRatingStyles = {});
  function oneRatingStyle() {
    document.querySelectorAll('.klaviyo-star-rating-widget style').forEach(function (style) {
      var text = style.textContent.trim();
      if (text.slice(0, 6) !== '@layer') return;
      var kept = layerStyles[text];
      if (!kept || !kept.isConnected) {
        kept = layerStyles[text] = document.createElement('style');
        kept.setAttribute('data-af-rating-style', '');
        kept.textContent = text;
        document.head.appendChild(kept);
      }
      style.remove();
    });
  }

  function tidyRatings(root) {
    oneRatingStyle();
    root.querySelectorAll(RATING).forEach(function (box) {
      box.querySelectorAll('.kl_reviews__star_rating_widget:not([role])').forEach(function (widget) {
        widget.setAttribute('role', 'img');
        if (widget.hasAttribute('href')) {
          widget.removeAttribute('href');
          widget.tabIndex = -1;
        }
      });
    });
  }

  /* The shopper has begun to swipe this card's photos and a video is on a later slide: its
     playlist (a few hundred bytes of text; on browsers without HLS the product's JSON) is asked
     for now, so that play() can be called the instant the slide is in view. No video data moves
     until then. Not under Save-Data, where nothing starts by itself. */
  function readyOnSwipe(track) {
    var video = track.querySelector('.af-card__slide ~ .af-card__slide [data-af-video]');
    if (!video) return;
    function ready() {
      track.removeEventListener('scroll', ready);
      if (autoplayAllowed() && !mqHover.matches) ensureSource(video).catch(function () {});
    }
    track.addEventListener('scroll', ready, { passive: true });
  }

  function init(root) {
    var newRatings = false;
    (root || document).querySelectorAll('.af-card:not([data-af-ready])').forEach(function (card) {
      card.setAttribute('data-af-ready', '');
      card.querySelectorAll('[data-af-video]').forEach(bindVideo);
      bindHover(card);
      var track = card.classList.contains('af-card--swipe') && card.querySelector('.af-card__track');
      if (track && mqSwipe.matches) {
        var extra = track.hasAttribute('data-af-extra');
        if (extra || track.querySelectorAll('.af-card__slide').length > 1) makeFocusable(track);
        if (extra && nearIO) nearIO.observe(track);
        readyOnSwipe(track);
      }
      if (card.querySelector('.af-card__rating:empty')) newRatings = true;
    });
    if (newRatings) askForRatings();
    tidyRatings(root || document);
  }

  window.afCardMedia = { init: init };

  /* Cards also arrive later: filters, "Load more", product recommendations */
  var pending = false;
  if ('MutationObserver' in window) {
    new MutationObserver(function (records) {
      if (pending) return;
      for (var i = 0; i < records.length; i++) {
        if (records[i].addedNodes.length) { pending = true; break; }
      }
      if (pending) requestAnimationFrame(function () { pending = false; init(document); });
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(document); });
  else init(document);
})();
